import React from 'react';

/**
 * Shared chrome for every /inventory page — the black header bar + ruby
 * gradient underline, matching the app's established page-header pattern
 * (see DealsPage.tsx for the purple version, PosConfigTab.tsx for the
 * emerald one). Ruby (#DC143C) is this suite's own accent, straight from the
 * mockup (`inventory-financing-suite.html`) so the Inventory section reads as
 * one visually distinct area of the admin.
 */

export const RUBY = '#DC143C';
export const RUBY_DEEP = '#9F1239';

export const InventoryHeader: React.FC<{
  icon: React.FC<any>;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  /** Sub-tab bar rendered INSIDE the black header, under the title row, above the ruby line. */
  tabs?: React.ReactNode;
  /** Breadcrumb root label — this chrome is reused outside /inventory (e.g.
   * business transfers), so the crumb is no longer hard-coded. */
  crumb?: string;
}> = ({ icon: Icon, title, subtitle, actions, tabs, crumb = 'Inventory' }) => (
  <div className="bg-black">
    <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center shrink-0">
          <Icon size={20} className="text-white" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <span>{crumb}</span>
            <span className="opacity-50">&rsaquo;</span>
            <span className="text-gray-300">{title}</span>
          </div>
          <h1 className="text-base md:text-lg font-semibold text-white truncate">{title}</h1>
          {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
    {tabs && <div className="px-4 md:px-6 pb-2">{tabs}</div>}
    <div style={{ background: `linear-gradient(90deg, #e8365a, ${RUBY}, ${RUBY_DEEP})`, height: '3px' }} />
  </div>
);

/**
 * Sub-tab bar for Sales and Procurement — lives INSIDE the black header bar
 * (TenantProfile-style: tabs blend with the black top bar), active tab as a
 * ruby-tinted pill. Render it on a black background only.
 */
export const InventorySubtabs: React.FC<{
  tabs: { id: string; label: string }[];
  active: string;
  onChange: (id: string) => void;
}> = ({ tabs, active, onChange }) => (
  <div className="flex items-center gap-1 overflow-x-auto">
    {tabs.map((t) => {
      const on = active === t.id;
      return (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition min-h-[38px] ${
            on ? '' : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
          style={on
            ? { background: 'rgba(220,20,60,0.16)', border: '1px solid rgba(220,20,60,0.5)', color: '#fda4af' }
            : { border: '1px solid transparent' }}
        >
          {t.label}
        </button>
      );
    })}
  </div>
);

/** Standard page shell: black header, ruby underline, light content area. */
export const InventoryPage: React.FC<{
  icon: React.FC<any>;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  /** Sub-tab bar (InventorySubtabs) shown inside the black header. */
  tabs?: React.ReactNode;
  children: React.ReactNode;
  /** DealsPage/DealEditPage/OrderDetail all render nested full pages that carry
   * their own black header — this suppresses the double header when that
   * happens (SalesPage's Deals sub-tab). */
  bare?: boolean;
  /**
   * Drop the 1400px reading cap and run edge to edge. For pages that are a
   * WORKSPACE rather than a document — the bundle builder, where the width is
   * spent on a picker and a running total, not on lines of prose that get
   * harder to read the wider they run.
   */
  wide?: boolean;
  /** Forwarded to InventoryHeader — see its doc comment. */
  crumb?: string;
}> = ({ icon, title, subtitle, actions, tabs, children, bare, wide, crumb }) => (
  <div className="min-h-screen bg-black -m-3 md:-m-6">
    {!bare && <InventoryHeader icon={icon} title={title} subtitle={subtitle} actions={actions} tabs={tabs} crumb={crumb} />}
    <div className="bg-stone-100 min-h-[calc(100vh-72px)] text-stone-800">
      <div className={`px-4 md:px-6 py-4 md:py-6 ${wide ? 'w-full' : 'max-w-[1400px] mx-auto'}`}>
        {children}
      </div>
    </div>
  </div>
);
